import { useEffect, useState } from "react";
import { connect } from "react-redux";
import Template from "./Template";
import { User } from "../model/user";
import { RootState } from "../model";
import { useActions } from "../redux/actions";
import * as usersActions from "../redux/actions/users";
import { Button, Dropdown, Form, Grid, Icon } from "semantic-ui-react";
import OpenDentalUserResult from "./OpenDentalUserResult";
import * as practiceActions from "../redux/actions/practice";
import { brands } from "../constants";
import Loading from "../Loading";

interface Props {
    auth: User;
    allPracticeList: any;
    loader: any;
    loaderGroups: any;
}

interface GroupAssignment {
    group: string;
    practices: string[];
}

function CreateOpenDentalUser(props: Props) {
    const UsersActions = useActions(usersActions);
    const practiceAction = useActions(practiceActions);

    const [practiceOpts, setPracticeOpts] = useState<any[]>([]);
    const [filteredPracticeOpts, setFilteredPracticeOpts] = useState<any[]>([]);
    const [regionList, setRegionList] = useState<any[]>([]);
    const [groupList, setGroupList] = useState<any[]>([]);
    const [result, setResult] = useState<any[]>([]);
    const [filters, setFilters] = useState({ brands: [] as string[], regions: [] as string[] });
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState("Password must not be empty");
    const [groupAssignments, setGroupAssignments] = useState<GroupAssignment[]>([{ group: "", practices: [] }]);

    const { loader, loaderGroups } = props;

    useEffect(() => {
        setTimeout(() => {
            const regions: any[] = [];
            const opts: any[] = [{ key: "all", value: "", text: "Select Practice", region: "", brand: "" }];
            practiceAction.getAllPractices().then((practices: any) => {
                (practices || []).forEach((item: any) => {
                    const practice = item.practice;
                    const regionName = practice.UdaPracticeInfo?.Region?.name;
                    const practiceBrand = practice.UdaPracticeInfo?.brand;
                    opts.push({ key: practice.practice, value: practice.practice, text: practice.practice, region: regionName, brand: practiceBrand });
                    if (!regions.some((r: any) => r.text === regionName)) {
                        regions.push({ text: regionName, value: regionName, key: regionName });
                    }
                });
                setPracticeOpts(opts);
                setRegionList(regions);
            });
            UsersActions.getOdUserGroups().then((groups: any) => {
                setGroupList(
                    (groups || []).map((item: any, idx: number) => ({
                        text: item.groupName,
                        value: item.groupName,
                        key: idx,
                    }))
                );
            });
        }, 500);
    }, []);

    // Re-apply filters whenever opts or filters change
    useEffect(() => {
        let filtered = practiceOpts;
        if (filters.brands.length) filtered = filtered.filter((o: any) => filters.brands.includes(o.brand));
        if (filters.regions.length) filtered = filtered.filter((o: any) => filters.regions.includes(o.region));
        setFilteredPracticeOpts(filtered);
        // Reset practice selections in all rows when filters change
        setGroupAssignments((prev) => prev.map((a) => ({ ...a, practices: [] })));
    }, [filters, practiceOpts]);

    const validatePassword = (pwd: string) => {
        if (pwd.length < 8) return "Password must be at least 8 characters long.";
        if (!/[A-Z]/.test(pwd)) return "Password must contain at least one uppercase letter.";
        if (!/[a-z]/.test(pwd)) return "Password must contain at least one lowercase letter.";
        if (!/[0-9]/.test(pwd)) return "Password must contain at least one number.";
        return "";
    };

    const handleGroupChange = (index: number, group: string) => {
        const updated = [...groupAssignments];
        updated[index] = { ...updated[index], group };
        setGroupAssignments(updated);
    };

    const handlePracticesChange = (index: number, practices: string[]) => {
        const updated = [...groupAssignments];
        updated[index] = { ...updated[index], practices };
        setGroupAssignments(updated);
    };

    const handleAddGroup = () => {
        setGroupAssignments([...groupAssignments, { group: "", practices: [] }]);
    };

    const handleRemoveGroup = (index: number) => {
        setGroupAssignments(groupAssignments.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (passwordError) { alert(passwordError); return; }
        if (!username) { alert("Username is required!"); return; }

        const validAssignments = groupAssignments.filter((a) => a.group && a.practices.length > 0);
        if (validAssignments.length === 0) {
            alert("At least one group with practices is required!");
            return;
        }

        const res = await UsersActions.createOpenDentalUser({
            username,
            password,
            groupAssignments: validAssignments,
        });
        setResult(res);
    };

    const availablePractices = filteredPracticeOpts.length > 0 ? filteredPracticeOpts : practiceOpts;
    const usedGroups = groupAssignments.map((a) => a.group).filter(Boolean);

    return (
        <Template activeLink="create-user">
            <div className="ui card">
                <div className="content">
                    <h2 className="float-left mr10">Create Open Dental User</h2>
                    <div className="topFilters">
                        <Dropdown
                            placeholder="Select Brand"
                            options={brands}
                            selection={true}
                            multiple={true}
                            search={true}
                            value={filters.brands}
                            onChange={(_: any, { value }: any) => setFilters({ ...filters, brands: value })}
                            name="brands"
                        />
                        <Dropdown
                            placeholder="Select Region"
                            options={regionList}
                            selection={true}
                            multiple={true}
                            search={true}
                            value={filters.regions}
                            onChange={(_: any, { value }: any) => setFilters({ ...filters, regions: value })}
                            name="regions"
                        />
                    </div>
                </div>
            </div>

            <Grid>
                <Grid.Row>
                    <Grid.Column computer={10} tablet={16} mobile={16}>
                        <div className="ui card">
                            <div className="content">
                                <Form>
                                    <Form.Group widths="equal">
                                        <Form.Input
                                            placeholder="Username"
                                            name="username"
                                            value={username}
                                            onChange={(_, { value }) => setUsername(value)}
                                        />
                                        <Form.Input
                                            placeholder="Password"
                                            name="password"
                                            type="password"
                                            value={password}
                                            error={password.length > 0 && passwordError ? passwordError : undefined}
                                            onChange={(_, { value }) => {
                                                setPassword(value);
                                                setPasswordError(validatePassword(value));
                                            }}
                                        />
                                    </Form.Group>

                                    <div className="mb15">
                                        <label>Group Assignments:</label>

                                        {groupAssignments.map((assignment, index) => {
                                            const groupOptions = groupList.map((g) => ({
                                                ...g,
                                                disabled: usedGroups.includes(g.value) && g.value !== assignment.group,
                                            }));

                                            return (
                                                <div
                                                    key={index}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        gap: 8,
                                                        marginBottom: 10,
                                                        padding: "10px 12px",
                                                        border: "1px solid #e0e0e0",
                                                        borderRadius: 6,
                                                        background: "#fafafa",
                                                    }}
                                                >
                                                    {/* Group selector */}
                                                    <div style={{ minWidth: 160 }}>
                                                        <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>Group</div>
                                                        <Dropdown
                                                            placeholder="Select Group"
                                                            selection={true}
                                                            search={true}
                                                            options={groupOptions}
                                                            value={assignment.group}
                                                            onChange={(_, { value }) => handleGroupChange(index, value as string)}
                                                            style={{ minWidth: 150 }}
                                                        />
                                                    </div>

                                                    {/* Practices selector */}
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>
                                                            Practices ({assignment.practices.length} selected)
                                                        </div>
                                                        <Dropdown
                                                            placeholder="Select Practices"
                                                            multiple={true}
                                                            fluid={true}
                                                            search={true}
                                                            selection={true}
                                                            options={availablePractices}
                                                            value={assignment.practices}
                                                            onChange={(_, { value }) => handlePracticesChange(index, value as string[])}
                                                        />
                                                    </div>

                                                    {/* Remove button */}
                                                    <div style={{ paddingTop: 22 }}>
                                                        <Button
                                                            type="button"
                                                            icon={true}
                                                            size="mini"
                                                            color="red"
                                                            basic={true}
                                                            disabled={groupAssignments.length === 1}
                                                            onClick={() => handleRemoveGroup(index)}
                                                            title="Remove this group assignment"
                                                        >
                                                            <Icon name="trash alternate outline" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        <Button
                                            type="button"
                                            size="mini"
                                            basic={true}
                                            color="blue"
                                            onClick={handleAddGroup}
                                        >
                                            <Icon name="plus" /> Add Group
                                        </Button>
                                    </div>

                                    <Form.Button primary={true} content="Submit" onClick={handleSubmit} style={{ marginTop: 10 }} />
                                </Form>
                            </div>
                        </div>
                    </Grid.Column>
                </Grid.Row>
            </Grid>

            {result?.length > 0 && (
                <OpenDentalUserResult
                    result={result}
                    message="User has been added successfully!"
                    handleClose={() => setResult([])}
                />
            )}
            {loader || loaderGroups ? <Loading /> : ""}
        </Template>
    );
}

function mapStateToProps(state: RootState) {
    return {
        auth: state.auth,
        allPracticeList: state.practice.allPracticeList,
        loader: state.app.loader,
        loaderGroups: state.app.loaderGroups,
    };
}

export default connect(mapStateToProps)(CreateOpenDentalUser);
